import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Repeat, Send, CheckCircle } from 'lucide-react';
import { EDI_PROCESS_STEPS } from '../config/siteContent';

const ICONS = { Upload, Repeat, Send, CheckCircle };

/**
 * EDIProcessAnimation - light theme.
 * Animated SVG flow showing data moving from your system → iConnect → trading partner.
 * Step indicator pill on top, content + visualization below.
 */
const EDIProcessAnimation = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay) return;
    const interval = setInterval(() => {
      setActiveStep((s) => (s + 1) % EDI_PROCESS_STEPS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [autoPlay]);

  return (
    <section
      id="how-it-works"
      className="relative py-24 lg:py-32 bg-bg-soft overflow-hidden"
    >
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 max-w-3xl mx-auto"
        >
          <p className="eyebrow">How It Works</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4 tracking-[-0.02em] text-balance">
            <span className="text-gradient-sky">EDI</span> in 4 simple steps
          </h2>
          <p className="text-lg text-text-secondary text-pretty">
            From your business system to your trading partner, in seconds. Automate
            document exchange, reduce manual entry, and improve transaction visibility.
          </p>
        </motion.div>

        {/* Step pills */}
        <div className="flex justify-center mb-12">
          <div className="bg-white border border-border-subtle rounded-full p-1.5 inline-flex gap-1 shadow-soft">
            {EDI_PROCESS_STEPS.map((step, idx) => (
              <button
                key={step.step}
                onClick={() => {
                  setActiveStep(idx);
                  setAutoPlay(false);
                }}
                className={`relative px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                  activeStep === idx
                    ? 'text-white'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {activeStep === idx && (
                  <motion.div
                    layoutId="activeStepBg"
                    className="absolute inset-0 bg-gradient-sky rounded-full shadow-glow-sky"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                  />
                )}
                <span className="relative">
                  {step.step}. {step.title}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Step content */}
          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              {EDI_PROCESS_STEPS.map((step, idx) =>
                idx === activeStep ? (
                  <motion.div
                    key={step.step}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 30 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-sky shadow-glow-sky flex items-center justify-center">
                        {React.createElement(ICONS[step.icon] || Upload, {
                          size: 22,
                          className: 'text-white',
                        })}
                      </div>
                      <span className="text-accent-primary font-mono text-sm font-bold">
                        Step {step.step} / 4
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-3xl sm:text-4xl text-text-primary mb-4 tracking-tight">
                      {step.title}
                    </h3>
                    <p className="text-text-secondary text-lg leading-relaxed mb-4">
                      {step.description}
                    </p>
                    <div className="bg-white border border-border-subtle shadow-soft rounded-lg px-4 py-2 inline-flex items-center gap-2 text-sm">
                      <CheckCircle size={14} className="text-accent-primary" />
                      <span className="text-text-primary font-mono">
                        {step.detail}
                      </span>
                    </div>
                  </motion.div>
                ) : null
              )}
            </AnimatePresence>
          </div>

          {/* SVG visualization */}
          <div className="lg:col-span-7">
            <div className="relative aspect-[4/3] card-elevated overflow-hidden">
              <svg viewBox="0 0 600 450" className="w-full h-full">
                <defs>
                  <linearGradient id="ediFlow" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#37ACE4" stopOpacity="0.2" />
                    <stop offset="50%" stopColor="#0EA5E9" stopOpacity="1" />
                    <stop offset="100%" stopColor="#37ACE4" stopOpacity="0.2" />
                  </linearGradient>
                  <filter id="ediGlow">
                    <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path
                    d="M 40 0 L 0 0 0 40"
                    fill="none"
                    stroke="#37ACE4"
                    strokeOpacity="0.07"
                  />
                </pattern>
                <rect width="600" height="450" fill="url(#grid)" />

                {/* Source system */}
                <motion.g
                  animate={{
                    opacity: activeStep === 0 ? 1 : 0.45,
                    scale: activeStep === 0 ? 1.05 : 1,
                  }}
                  style={{ transformOrigin: '90px 225px' }}
                  transition={{ duration: 0.5 }}
                >
                  <rect
                    x="20"
                    y="180"
                    width="140"
                    height="90"
                    rx="12"
                    fill="#FFFFFF"
                    stroke="#37ACE4"
                    strokeWidth="2"
                    filter="url(#ediGlow)"
                  />
                  <text
                    x="90"
                    y="215"
                    textAnchor="middle"
                    fill="#0F172A"
                    fontSize="14"
                    fontWeight="700"
                  >
                    Your System
                  </text>
                  <text x="90" y="240" textAnchor="middle" fill="#475569" fontSize="11">
                    ERP / Database
                  </text>
                </motion.g>

                {/* iConnect hub */}
                <motion.g
                  animate={{
                    opacity:
                      activeStep === 1 ? 1 : activeStep === 0 ? 0.55 : 0.75,
                    scale: activeStep === 1 ? 1.1 : 1,
                  }}
                  style={{ transformOrigin: '300px 225px' }}
                  transition={{ duration: 0.5 }}
                >
                  <circle
                    cx="300"
                    cy="225"
                    r="55"
                    fill="#37ACE4"
                    fillOpacity="0.15"
                    stroke="#37ACE4"
                    strokeWidth="2"
                    filter="url(#ediGlow)"
                  />
                  <circle cx="300" cy="225" r="35" fill="#0EA5E9" filter="url(#ediGlow)" />
                  <text
                    x="300"
                    y="220"
                    textAnchor="middle"
                    fill="white"
                    fontSize="12"
                    fontWeight="800"
                  >
                    iConnect
                  </text>
                  <text x="300" y="234" textAnchor="middle" fill="white" fontSize="10">
                    Transform
                  </text>
                </motion.g>

                {/* Partner */}
                <motion.g
                  animate={{
                    opacity: activeStep >= 2 ? 1 : 0.45,
                    scale: activeStep >= 2 ? 1.05 : 1,
                  }}
                  style={{ transformOrigin: '510px 225px' }}
                  transition={{ duration: 0.5 }}
                >
                  <rect
                    x="440"
                    y="180"
                    width="140"
                    height="90"
                    rx="12"
                    fill="#FFFFFF"
                    stroke="#37ACE4"
                    strokeWidth="2"
                    filter="url(#ediGlow)"
                  />
                  <text
                    x="510"
                    y="215"
                    textAnchor="middle"
                    fill="#0F172A"
                    fontSize="14"
                    fontWeight="700"
                  >
                    Partner
                  </text>
                  <text x="510" y="240" textAnchor="middle" fill="#475569" fontSize="11">
                    Customer / Supplier
                  </text>
                </motion.g>

                {/* Connections */}
                <motion.line
                  x1="160"
                  y1="225"
                  x2="245"
                  y2="225"
                  stroke="url(#ediFlow)"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{
                    pathLength: 1,
                    opacity: activeStep >= 1 ? 1 : 0.3,
                  }}
                  transition={{ duration: 0.5 }}
                />
                <motion.line
                  x1="355"
                  y1="225"
                  x2="440"
                  y2="225"
                  stroke="url(#ediFlow)"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{
                    pathLength: 1,
                    opacity: activeStep >= 2 ? 1 : 0.3,
                  }}
                  transition={{ duration: 0.5 }}
                />

                {/* Animated particles */}
                <motion.circle
                  cx="0"
                  cy="225"
                  r="5"
                  fill="#0EA5E9"
                  filter="url(#ediGlow)"
                  animate={{
                    cx: [160, 245, 300, 355, 440],
                    opacity: [0, 1, 1, 1, 0],
                  }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                />
                <motion.circle
                  cx="0"
                  cy="225"
                  r="4"
                  fill="#0EA5E9"
                  filter="url(#ediGlow)"
                  animate={{
                    cx: [160, 245, 300, 355, 440],
                    opacity: [0, 1, 1, 1, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear',
                    delay: 1,
                  }}
                />

                {/* Acknowledgment */}
                {activeStep === 3 && (
                  <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <motion.circle
                      cx="0"
                      cy="280"
                      r="4"
                      fill="#37ACE4"
                      filter="url(#ediGlow)"
                      animate={{ cx: [440, 355, 300, 245, 160] }}
                      transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }}
                    />
                    <text
                      x="300"
                      y="310"
                      textAnchor="middle"
                      fill="#37ACE4"
                      fontSize="11"
                      fontWeight="600"
                    >
                      ← Acknowledgment
                    </text>
                  </motion.g>
                )}

                {/* Top labels */}
                <text x="90" y="160" textAnchor="middle" fill="#94A3B8" fontSize="10" fontWeight="700">
                  1. SEND
                </text>
                <text x="200" y="160" textAnchor="middle" fill="#94A3B8" fontSize="10" fontWeight="700">
                  2. TRANSFORM
                </text>
                <text x="400" y="160" textAnchor="middle" fill="#94A3B8" fontSize="10" fontWeight="700">
                  3. DELIVER
                </text>
                <text x="510" y="160" textAnchor="middle" fill="#94A3B8" fontSize="10" fontWeight="700">
                  4. CONFIRM
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EDIProcessAnimation;
